<template>
  <v-dialog :model-value="modelValue" @update:model-value="closeMediaModal" max-width="850px" width="95%" persistent>
    <v-card v-if="activeModule" class="rounded-lg overflow-hidden d-flex flex-column" style="max-height: 85vh;">

      <!-- Шапка модального окна в фирменном стиле (Индиго) -->
      <v-card-title class="bg-indigo-darken-4 px-4 px-md-6 py-3 text-white d-flex align-start align-sm-center justify-space-between w-100">
        <div class="d-flex align-start align-sm-center flex-column flex-sm-row text-truncate mr-2">
          <div class="d-flex align-center mb-1 mb-sm-0">
            <v-icon start class="mr-2">mdi-folder-image</v-icon>
            <span class="text-subtitle-1 font-weight-bold text-grey-lighten-3 mr-1">Медиатека:</span>
          </div>
          <span class="text-subtitle-1 text-sm-h6 font-weight-black text-truncate text-white pl-0 pl-sm-1">
            {{ activeModule.courseModuleName || activeModule.title }}
          </span>
        </div>
        <v-btn icon="mdi-close" variant="text" color="white" density="compact" class="ml-auto ma-2" @click="closeMediaModal"></v-btn>
      </v-card-title>

      <!-- Основной контент галереи -->
      <v-card-text class="pa-4 pb-6 pa-md-6 bg-grey-lighten-4 overflow-y-auto">
        <v-row no-gutters>

          <!-- ЛЕВАЯ КОЛОНКА: Сетка загруженных видеофайлов из БД -->
          <v-col cols="12" md="7" class="pa-2 mb-6">
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-3 mb-2">Загруженные отчетные материалы</div>

            <div class="media-gallery-grid" v-if="videoFiles && videoFiles.length > 0">
              <v-card
                v-for="(file, idx) in videoFiles"
                :key="idx"
                variant="outlined"
                class="bg-white rounded-md overflow-hidden position-relative media-preview-card ma-2"
              >
                <!-- Область HLS Видеоплеера с авто-инициализацией на событии @vue:mounted -->
                <div class="video-preview-zone d-flex align-center justify-center bg-grey-darken-4 rounded-md overflow-hidden" style="height: 140px;">
                  <video
                    :poster="'https://esse2.eurekacenter.ru/' + formatServerPath(file.posterName)"
                    controls
                    playsinline
                    class="w-100 h-100 object-cover"
                    @vue:mounted="(vnode) => $emit('init-player', vnode.el, 'https://esse2.eurekacenter.ru/' + formatServerPath(file.sourceHlsIndex))"
                  >
                    <source :src="'https://esse2.eurekacenter.ru' + formatServerPath(file.fileUrl || file.sourceHlsIndex)" type="application/x-mpegURL">
                  </video>
                </div>

                <!-- Меню переключателя 4 разрешений из .m3u8 -->
                <div v-if="availableLevels.length > 0" class="mt-2 text-right px-1">
                  <v-menu location="bottom end">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        size="x-small"
                        variant="outlined"
                        color="grey-darken-3"
                        prepend-icon="mdi-cog-outline"
                        class="rounded-md font-weight-bold"
                        style="height: 24px; border: 1px solid rgba(0,0,0,0.15) !important;"
                      >
                        Качество: {{ currentLevelIndex === -1 ? 'Авто' : availableLevels.find(l => l.index === currentLevelIndex)?.name || 'Поток' }}
                      </v-btn>
                    </template>

                    <v-list density="compact" class="rounded-md border pa-1 bg-white">
                      <v-list-item
                        @click="$emit('change-quality', -1)"
                        :class="{ 'bg-indigo-lighten-5 font-weight-bold text-indigo-darken-4': currentLevelIndex === -1 }"
                        class="rounded-sm"
                      >
                        <v-list-item-title class="text-caption" style="font-size: 0.75rem !important;">
                          Авто (Адаптивное)
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        v-for="level in availableLevels"
                        :key="level.index"
                        @click="$emit('change-quality', level.index)"
                        :class="{ 'bg-indigo-lighten-5 font-weight-bold text-indigo-darken-4': currentLevelIndex === level.index }"
                        class="rounded-sm"
                      >
                        <v-list-item-title class="text-caption" style="font-size: 0.75rem !important;">
                          {{ level.name }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <!-- Подпись и блок статусов под плеером -->
                <div class="pa-2 bg-grey-lighten-5 border-top text-center">
                  <span class="text-caption text-grey-darken-2 font-weight-medium text-truncate d-block mb-1">
                    {{ file.originalFileName || 'Видеозапись проекта' }}
                  </span>

                  <div v-if="file.status === 'AUDIO_TRANSCRIPTED'" class="transcription-badge my-1" @click="$emit('open-transcription', file)" style="cursor: pointer;">
                    <v-chip size="x-small" color="error" class="font-weight-black">AUDIO_TRANSCRIBED</v-chip>
                  </div>
                  <div v-else class="my-1">
                    <v-chip size="x-small" variant="outlined" color="grey-darken-2">{{ file.status }}</v-chip>
                  </div>

                  <!-- Кнопка удаления файла -->
                  <v-btn
                    icon="mdi-delete-outline"
                    size="x-small"
                    variant="text"
                    color="error"
                    class="mt-1"
                    title="Удалить этот файл из отчета"
                    @click="$emit('delete-file', file.id)"
                  ></v-btn>
                </div>
              </v-card>
            </div>

            <!-- Заглушка, если видео файлов нет -->
            <div v-else class="d-flex flex-column align-center justify-center bg-white rounded-md border py-12 px-4 text-center h-100 min-h-220">
              <v-icon size="large" color="grey-lighten-1" class="mb-2">mdi-image-off-outline</v-icon>
              <span class="text-caption text-grey font-italic">Файлы видеоотчетов отсутствуют</span>
            </div>
          </v-col>

          <!-- ПРАВАЯ КОЛОНКА: Интерфейс загрузки новых видеофайлов -->
          <v-col cols="12" md="5" class="pa-2 border-left-light-modal">
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-3 mb-3">Прикрепить новые материалы</div>
            <div class="d-flex flex-column gap-2">
              <input type="file" ref="videoInputRef" accept="video/*" class="d-none" @change="(e) => $emit('upload-video', e)" />

              <v-btn
                prepend-icon="mdi-video-outline"
                variant="outlined"
                color="amber-darken-3"
                block
                class="rounded-md font-weight-bold py-5 text-body-2 mt-2"
                :disabled="downloadAnimation"
                @click="videoInputRef?.click()"
              >
                Прикрепить видеоролик
              </v-btn>
            </div>

            <!-- Круговой или линейный индикатор загрузки с процентами -->
            <v-expand-transition>
              <div class="mt-4 pa-3 bg-white rounded-md border text-center" v-if="downloadAnimation">
                <div class="text-caption font-weight-bold text-grey-darken-3 mb-2">Загрузка файла: {{ uploadPercentage }}%</div>
                <v-progress-linear :model-value="uploadPercentage" color="success" height="6" rounded class="mb-2"></v-progress-linear>
              </div>
            </v-expand-transition>
          </v-col>

        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script setup>
import { ref, defineProps, defineEmits } from 'vue' // <-- 1. Исправлено: Добавлен импорт макросов


// Входящие параметры и события для связи с родителем
defineProps({
  modelValue: { type: Boolean, required: true },
  activeModule: { type: Object, default: null },
  videoFiles: { type: Array, default: () => [] },
  downloadAnimation: { type: Boolean, default: false },
  uploadPercentage: { type: Number, default: 0 },
  availableLevels: { type: Array, default: () => [] },
  currentLevelIndex: { type: Number, default: -1 }
})

const emit = defineEmits([
  'update:modelValue',
  'init-player',
  'change-quality',
  'upload-video',
  'delete-file',
  'open-transcription'
])

const videoInputRef = ref(null)

// Функция принимает абсолютный путь сервера и вырезает базовую директорию
const formatServerPath = (path) => {
  if (!path) return ''
  return path.replace(/^\/{1,2}var\/www\/esse2/, '')
}

// Корректное закрытие модального окна медиатеки
const closeMediaModal = () => {
  emit('update:modelValue', false)
  if (videoInputRef.value) {
    videoInputRef.value.value = ''
  }
}


</script>
<style scoped>
/* Сетка для отображения карточек отчетных материалов */
.media-gallery-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Стилизация карточки превью видео */
.media-preview-card {
  width: calc(50% - 16px);
  min-width: 240px;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  transition: box-shadow 0.2s ease-in-out;
}

.media-preview-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Зона видеоплеера */
.video-preview-zone {
  position: relative;
  width: 100%;
}

.object-cover {
  object-fit: cover;
}

/* Граница разделения колонок в модальном окне */
@media (min-width: 960px) {
  .border-left-light-modal {
    border-left: 1px solid rgba(0, 0, 0, 0.08) !important;
  }
}

.min-h-220 {
  min-height: 220px;
}

/* Ограничение строки названия файла */
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Стилизация плашки транскрибации аудио */
.transcription-badge {
  display: inline-block;
  background-color: #ffebee;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.75rem;
  transition: background-color 0.2s;
}

.transcription-badge:hover {
  background-color: #ffcdd2;
}

.gap-2 {
  gap: 8px;
}
</style>